/**
 * Shared Cash Position Summary grid/filter types (DCP-145 / DCP-144).
 *
 * The column model is produced ENTIRELY server-side by the Cash Position RPCs
 * (facility / pack-subtotal / total rows, pre-ordered) and mapped 1:1 by the
 * consuming hook. This file holds only the shared filter shape consumed by the
 * grid and the filter bar — no client-side aggregation remains.
 */

/**
 * Cash Position Summary filter selection (DCP-145). Both are multi-select and
 * passed straight to the RPC. Empty on BOTH means "All" (every pack expanded +
 * every standalone facility); packs and individual facilities combine as a UNION.
 */
export interface CashPositionFilterState {
  /** Selected pack ids (each returned expanded: member columns + subtotal). */
  packIds: string[]
  /** Selected individual facility ids (each returned as its own column). */
  facilityIds: string[]
}

/**
 * Whether the frozen "Combined Total" column is worth rendering for a given
 * filter selection (DCP-293).
 *
 * It is hidden in exactly two cases, where the combined total would be a verbatim
 * copy of a column already on screen:
 *   - a SINGLE pack and no individual facilities — the pack's own subtotal column
 *     already aggregates every facility in scope;
 *   - a SINGLE facility and no packs — that facility's column IS the whole scope.
 *
 * Every wider selection keeps it: two or more packs/facilities, a pack combined
 * with a facility, and the unfiltered "All" view all aggregate across more than
 * one on-screen column.
 *
 * @param filters - the current pack/facility selection
 * @returns true when the Combined Total column should be rendered
 */
export function shouldShowCombinedTotal({
  packIds,
  facilityIds,
}: CashPositionFilterState): boolean {
  const singlePack = packIds.length === 1 && facilityIds.length === 0
  const singleFacility = facilityIds.length === 1 && packIds.length === 0
  return !singlePack && !singleFacility
}
